import {
	AfterViewInit,
	Component,
	ElementRef,
	inject,
	Input,
	input,
	viewChild,
	ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule, NgForm, ReactiveFormsModule } from "@angular/forms";
import { KeyboardService } from "common/keyboard.service";
import { ItemEditor } from "common/list/item-editor";
import { TrimDirective } from "common/trim.directive";
import { MaxValidatorDirective } from "common/form/max.directive";
import { KeyUpDirective } from "common/key-up.directive";

import { TranslateModule } from "@ngx-translate/core";

@Component({
	selector: "stages-add",
	templateUrl: "./add.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, FormsModule, TrimDirective, MaxValidatorDirective, KeyUpDirective, TranslateModule],
})
export class AddComponent extends ItemEditor<string> implements AfterViewInit {
	private keyboardService = inject(KeyboardService);

	readonly maxLength = input(255);

	readonly pattern = input<string>();

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input() patternMessageKey?: string;

	readonly focusOnInit = input<boolean>(false);

	existsMessageKey = "add.item.name.existing";
	itemExist = false;

	readonly input = viewChild.required("input", { read: ElementRef });

	readonly form = viewChild.required<NgForm>("form");

	name?: string;
	wasNonEmpty = false;
	private mouseDown = false;

	ngAfterViewInit(): void {
		if (this.focusOnInit()) {
			this.focus();
		}
	}

	focus(): void {
		this.input().nativeElement.focus();
	}

	onKeyUp(event: KeyboardEvent): void {
		if (this.keyboardService.isReturnKeyPressed(event)) {
			this.enter();
		} else if (this.keyboardService.isEscapeKeyPressed(event)) {
			this.cancel();
		}
	}

	cancel(): void {
		this.customErrors = {};
		this.name = "";
		this.wasNonEmpty = false;
		this.form().reset();
		// TODO: The 'emit' function requires a mandatory void argument
		this.cancelled.emit();
	}

	onMouseDown(): void {
		this.mouseDown = true;
	}

	blur(): void {
		if (!this.mouseDown) {
			const form = this.form();
			if (this.name && this.name !== "" && form.valid) {
				this.submitted.emit(this.name);
				this.name = "";
				this.wasNonEmpty = false;
				form.reset();
			} else {
				this.focusLost.emit();
			}
		}
		this.mouseDown = false;
	}

	change(): void {
		super.onChange();

		// Workaround for IE/Angular bug
		// In IE 11, the "input" event erroneously fires as soon as the input element is focused.
		// See also: https://github.com/angular/angular/issues/14440#issuecomment-350812348
		// ...which will make the placeholder text disappear (known bug already in Stages 7.1) and the "Required" validation error appear
		// before the user has changed anything. This here will fix the second problem.
		if (this.name && this.name.length > 0) {
			this.wasNonEmpty = true;
			this.itemExist = false;
		}
	}

	private enter(): void {
		const form = this.form();
		if (this.name && form.valid) {
			this.submitted.emit(this.name);
			this.name = "";
			this.wasNonEmpty = false;
			form.reset();
		}
	}
}
